<template>
  <div id="thread" class="thread">
    <!-- Main thread comment -->
    <board-comment v-if="comment.content" :id="id" />
    <!-- Replies to main comment -->
    <board-comment v-for="reply in replies" :id="reply.id" :key="reply.id" />
    <!-- New reply -->
    <div class="input" @click="$refs.commentEditor.startEditing()">
      <Avatar
        :rounded="true"
        :color="getColor"
        :src="getAvatarUrl || getGravatarUrl"
        :alt="getShortName"
        :initials="getShortName"
      />
      <rich-text-editor
        ref="commentEditor"
        class="commentInput"
        :content="content"
        :editable="true"
        :activate-on-double-click="false"
        :extension-options="extensionOptions"
        @focus="onFocusReply"
        @update="onUpdateReply"
        @submit="onSave"
      />
    </div>
    <div v-if="focused" class="buttons">
      <div class="button cancel" @click="onCancel">Cancel</div>
      <div class="button" :disabled="disabled" @click="onSave">
        {{ comment.content ? 'Reply' : 'Post' }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { Avatar } from '@ui/components/avatar';
import RichTextEditor from '../../all/RichText/RichTextEditor.vue';
import BoardComment from './BoardComment.vue';
import requests from '../../../vuex/requests.js';

export default {
  compatConfig: {
    MODE: 2,
  },
  components: {
    BoardComment,
    Avatar,
    RichTextEditor,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      focused: false,
      extensionOptions: {
        hardbreak: true,
        bold: false,
        italic: false,
        underline: false,
        horizontalRule: false,
        customLink: false,
        history: true,
        placeholder: {
          text: 'Reply…',
        },
        dropEventHandler: true,
        customMention: false,
        // codeBlockHighlight: false,
        // blockquote: false,
      },
      delayOptions: { show: 100, hide: 100 },
      disabled: true,
      replyText: null, // Unsaved reply text for this comment
    };
  },

  computed: {
    ...mapGetters('user/account', [
      'getId',
      'getName',
      'getShortName',
      'getColor',
      'getGravatarUrl',
      'getAvatarUrl',
    ]),
    ...mapGetters('project/data/comments', {
      getComment: 'getNode',
      getComments: 'getNodes',
    }),
    ...mapGetters('application', ['getCommentText']),
    ...mapGetters('application/modals', ['getCommentPopover']),
    ...mapGetters('user/projects', { getOpenProject: 'getOpen' }),

    /**
     * The comment (thread)
     */
    comment() {
      return this.getComment(this.id);
    },

    /**
     * The various replies to the the main comment
     */
    replies() {
      const { children } = this.comment;
      return this.getComments(children);
    },

    /**
     * Whether the menu is open or not
     */
    open() {
      return this.getCommentPopover === this.id;
    },

    /**
     * The starting content for the rich text editor (for thread or reply)
     */
    content() {
      if (!this.comment.content) {
        return this.getCommentText;
      }
      return this.replyText;
    },
  },

  watch: {
    open(newVal, oldVal) {
      if (newVal) {
        if (!this.comment.content) {
          console.log('BoardCommentThread::open(). Start editing on editor...');
          setTimeout(() => {
            this.$refs.commentEditor.startEditing('end');
          }, 100);
        }
      } else {
        this.focused = !!this.replyText;
        if (this.focused) {
          setTimeout(() => {
            this.$refs.commentEditor.startEditing('end');
          }, 100);
        }
      }
    },
  },

  /**
   * Vue lifecycle hook before destroy.
   */
  beforeUnmount() {
    console.log('Before destroy called');
    // Store any unsaved comment text for the creation of next comment thread
    if (this.$refs.commentEditor.isContentEmpty()) {
      this.setCommentText(null);
    } else {
      this.setCommentText(this.$refs.commentEditor.getContent());
    }
  },

  methods: {
    ...mapActions('project/data/comments', {
      updateComment: 'customUpdate',
      createComment: 'customCreate',
    }),
    ...mapActions('project/config/comments', { setOpenComment: 'setOpen' }),
    ...mapMutations('application', ['setCommentText']),

    /**
     * Event handler for custom rich text editor focus event.
     */
    onFocusReply() {
      this.disabled = this.$refs.commentEditor.isContentEmpty();
      this.focused = true;
    },

    /**
     * Event handler for custom rich text editor update event.
     */
    onUpdateReply(content) {
      this.disabled = this.$refs.commentEditor.isContentEmpty();

      if (this.comment.content) {
        this.replyText = content;
      }
    },

    /**
     * Event handler for button save click event.
     */
    onSave() {
      const content = this.$refs.commentEditor.getContent();
      // Save the reply as main content
      if (!this.comment.content) {
        this.updateComment({
          id: this.id,
          props: { content },
          apply: true,
        }).then(response => {
          requests.projects.createCommentNotification({
            hash: this.getOpenProject,
            content,
          });
        });
        // Clear saved comment text for next thread
        this.setCommentText(null);
      } else {
        // Save a new reply
        const tId = this.id;
        const tIndex = '-';
        const props = { content, userId: this.getId };
        this.createComment({ props, tId, tIndex, apply: true }).then(
          response => {
            requests.projects.createCommentNotification({
              hash: this.getOpenProject,
              content,
            });
          }
        );
        // Clear saved reply text for next reply
        this.replyText = null;
      }
      // Save complete, clear the content
      this.$refs.commentEditor.setContent(null);
      this.$refs.commentEditor.blur();
      this.focused = false;
    },

    /**
     * Event handler: Cancel button is clicked
     */
    onCancel() {
      // Reset the content to empty
      this.$refs.commentEditor.setContent(null);
      this.$refs.commentEditor.blur();
      this.focused = false;
      // Close the comment modal if cancelling when there is no thread content
      if (!this.comment.content) {
        this.setOpenComment({ id: null, apply: true });
      } else {
        this.replyText = null;
      }
    },
  },
};
</script>

<style lang="scss">
.thread .editor_wrapper .editor p {
  font-size: 14px;
  color: #2c3233;
}
.thread .input {
  display: flex;
  padding: 20px;
  margin: 20px 0 0 0;

  &:first-child {
    margin: 0;
  }

  border-top: 1px solid #242929;
  .commentInput {
    margin: 4px 0 0 10px;
    width: 260px;
    p {
      color: #bcbcbc;
      &.is-editor-empty:first-child::before {
        color: #474e4e;
      }
    }
  }
}
</style>
<style scoped lang="scss">
.buttons {
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  .button {
    padding: 8px 12px;
    border-radius: 3px;
    display: inline-block;
    color: #fff;
    cursor: pointer;
    font-size: 13px;
    position: relative;
    background: #bf751d;
    border: none;
    min-width: 70px;
    text-align: center;
    margin-left: 10px;
  }
  .button.cancel {
    border: 1px solid #6e797b;
    background: none;
    color: #a7a7a7;
  }

  .button[disabled] {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #aaa;
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>
